import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Lock, Mail } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";
import { useBooking } from "@/lib/bookingStore";
import { PITCH_PASSWORD, EMAIL } from "@/lib/constants";

const EASE = [0.16, 1, 0.3, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
    <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE, delay }}
        className={className}
    >
        {children}
    </motion.div>
);

const Overline = ({ children }) => (
    <p className="font-mono-x mb-6 text-xs uppercase tracking-[0.25em] text-zinc-500">
        {children}
    </p>
);

const Gate = ({ onUnlock }) => {
    const [value, setValue] = useState("");
    const [error, setError] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const submit = (e) => {
        e.preventDefault();
        if (value.trim().toLowerCase() === PITCH_PASSWORD.toLowerCase()) {
            sessionStorage.setItem("rare_pitch_unlocked", "1");
            onUnlock();
        } else {
            setError(true);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center px-6">
            <motion.form
                data-testid="pitch-gate-form"
                onSubmit={submit}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE }}
                className="w-full max-w-md"
            >
                <div className="mb-10 flex flex-col items-center text-center">
                    <LogoMark size={44} animated className="text-white" />
                    <p className="font-mono-x mt-6 text-xs uppercase tracking-[0.25em] text-zinc-500">
                        [ Private — Access Required ]
                    </p>
                    <h1 className="font-display mt-4 text-3xl font-black tracking-tighter text-white sm:text-4xl">
                        The RaRe Pitch
                    </h1>
                    <p className="mt-4 max-w-xs text-sm leading-relaxed text-zinc-400">
                        This presentation is private. Enter the access code to continue.
                    </p>
                </div>

                <motion.div
                    animate={error ? { x: [0, -8, 8, -6, 6, 0] } : {}}
                    transition={{ duration: 0.4 }}
                    className={`flex items-center gap-3 rounded-full border bg-[#0c0c0c] px-5 py-4 transition-colors duration-300 ${
                        error ? "border-red-500/50" : "border-white/15 focus-within:border-white/50"
                    }`}
                >
                    <Lock className="h-4 w-4 text-zinc-500" />
                    <input
                        ref={inputRef}
                        data-testid="pitch-password-input"
                        type="password"
                        value={value}
                        onChange={(e) => {
                            setValue(e.target.value);
                            setError(false);
                        }}
                        placeholder="Access code"
                        className="w-full bg-transparent font-mono-x text-sm uppercase tracking-[0.2em] text-white placeholder:text-zinc-600 focus:outline-none"
                    />
                </motion.div>

                {error && (
                    <p
                        data-testid="pitch-password-error"
                        className="font-mono-x mt-3 text-center text-[11px] uppercase tracking-[0.2em] text-red-400"
                    >
                        Incorrect code — try again
                    </p>
                )}

                <button
                    data-testid="pitch-unlock-button"
                    type="submit"
                    className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-white px-8 py-4 font-display text-base font-bold text-[#050505] transition-transform duration-300 hover:scale-[1.02]"
                >
                    Unlock Presentation
                    <span className="grid h-7 w-7 place-items-center rounded-full bg-[#050505] text-white transition-transform duration-300 group-hover:rotate-45">
                        <ArrowUpRight className="h-4 w-4" />
                    </span>
                </button>

                <button
                    data-testid="pitch-back-home"
                    type="button"
                    onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = "/")}
                    className="mt-8 flex w-full items-center justify-center gap-2 font-mono-x text-[11px] uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-300 hover:text-white"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to site
                </button>
            </motion.form>
        </div>
    );
};

const SERVICES = [
    {
        n: "01",
        title: "Website Design & Development",
        body: "Product-grade websites and web apps, designed and engineered in-house. No templates, no page builders — built to load fast and convert.",
    },
    {
        n: "02",
        title: "Digital Marketing",
        body: "Full-funnel campaigns that turn attention into revenue — paid, organic, and email — with every rupee and click measured.",
    },
    {
        n: "03",
        title: "Business Streamlining",
        body: "Automations that reply to leads in seconds, onboard clients while you sleep, and kill the manual work eating your week.",
    },
];

const PROCESS = [
    { n: "01", title: "Discover", body: "We interrogate the idea — audience, offer, numbers. If it won't make money, we say so before you spend anything." },
    { n: "02", title: "Design", body: "Brand, website and product experience designed as one system — not three vendors pointing at each other." },
    { n: "03", title: "Build", body: "We ship the site and wire the automations underneath it — lead capture, instant follow-ups, client onboarding." },
    { n: "04", title: "Launch & Market", body: "Campaigns go live. We watch the data, tune the funnel, and keep scaling what works." },
];

const PRINCIPLES = ["Senior only", "Zero handoffs", "Here after launch", "Accountable to revenue"];

const Deck = () => {
    const navigate = useNavigate();
    const openBooking = useBooking((s) => s.openBooking);

    return (
        <div data-testid="pitch-deck" className="relative">
            <header className="fixed inset-x-0 top-0 z-40 border-b border-white/10 bg-black/40 backdrop-blur-xl">
                <div className="flex h-16 items-center justify-between px-6 md:h-20 md:px-12">
                    <button
                        data-testid="pitch-nav-home"
                        onClick={() => navigate("/")}
                        className="group flex items-center gap-2.5 font-display text-xl font-black tracking-tight text-white md:text-2xl"
                    >
                        <LogoMark size={20} className="text-white transition-transform duration-500 group-hover:rotate-90" />
                        RaRe<span className="align-super text-[0.5em] text-white/40">®</span>
                    </button>
                    <span className="font-mono-x text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                        [ The Pitch ]
                    </span>
                    <button
                        data-testid="pitch-book-call-top"
                        onClick={openBooking}
                        className="group hidden items-center gap-2 rounded-full bg-white px-5 py-2.5 font-display text-sm font-bold text-[#050505] transition-transform duration-300 hover:scale-105 sm:inline-flex md:px-6 md:py-3"
                    >
                        Book a Call
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                    </button>
                </div>
            </header>

            {/* Cover */}
            <section className="flex min-h-screen flex-col justify-center px-6 pt-24 md:px-12">
                <Reveal>
                    <Overline>[ Private Presentation — Confidential ]</Overline>
                </Reveal>
                <h1 className="font-display font-black uppercase leading-[0.9] tracking-tighter text-[13vw] lg:text-[8vw]">
                    <Reveal delay={0.05}>
                        <span className="block">We don't sell</span>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <span className="block text-outline">websites.</span>
                    </Reveal>
                    <Reveal delay={0.19}>
                        <span className="block">
                            We build businesses that{" "}
                            <span className="font-logo font-extrabold text-white [-webkit-text-stroke:0px]">win.</span>
                        </span>
                    </Reveal>
                </h1>
                <Reveal delay={0.3}>
                    <p className="mt-10 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg">
                        For founders who have the idea — and need the team that actually
                        ships it, launches it, and makes it grow.
                    </p>
                </Reveal>
            </section>

            {/* Who we are */}
            <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32">
                <div className="grid gap-16 md:grid-cols-12">
                    <div className="md:col-span-4">
                        <Reveal><Overline>[ Who we are ]</Overline></Reveal>
                    </div>
                    <div className="md:col-span-8">
                        <Reveal>
                            <h2 className="font-display max-w-3xl text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
                                A senior, four-person studio.
                                <br />
                                <span className="text-outline">No middlemen.</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-10 max-w-3xl text-base leading-relaxed text-zinc-400 md:text-xl">
                                Over twelve years combined across MNC product engineering
                                and growth marketing — shipping software at scale and running
                                campaigns that had to justify every rupee. The people you
                                meet on the first call are the people who design, build and
                                market your product.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* The problem */}
            <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32">
                <div className="grid gap-16 md:grid-cols-12">
                    <div className="md:col-span-4">
                        <Reveal><Overline>[ The problem ]</Overline></Reveal>
                    </div>
                    <div className="md:col-span-8">
                        <Reveal>
                            <h2 className="font-display max-w-3xl text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
                                Ideas die in the gap between vision and execution.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-10 max-w-3xl text-base leading-relaxed text-zinc-400 md:text-xl">
                                A web designer who doesn't understand marketing. A marketer
                                who can't touch the product. Freelancers who vanish after
                                launch. The result is slow launches, leaky funnels, and no
                                one who owns the outcome.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-8 max-w-3xl font-display text-2xl font-bold tracking-tight text-white md:text-3xl">
                                RaRe is the single team that owns the whole thing.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* What we do */}
            <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32">
                <Reveal><Overline>[ What we do ]</Overline></Reveal>
                <Reveal>
                    <h2 className="font-display mb-16 max-w-3xl text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl md:mb-24">
                        Everything after the idea.
                    </h2>
                </Reveal>
                <div className="grid gap-6 md:grid-cols-3 md:gap-8">
                    {SERVICES.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.08}>
                            <div className="h-full border border-white/10 bg-[#0c0c0c] p-8 transition-colors duration-500 hover:border-white/30 md:p-10">
                                <span className="font-mono-x text-xs tracking-[0.2em] text-zinc-500">[{s.n}]</span>
                                <h3 className="font-display mt-6 text-2xl font-bold tracking-tight text-white md:text-3xl">
                                    {s.title}
                                </h3>
                                <p className="mt-4 text-base leading-relaxed text-zinc-400">{s.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* Process */}
            <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32">
                <Reveal><Overline>[ How it works ]</Overline></Reveal>
                <Reveal>
                    <h2 className="font-display mb-16 max-w-3xl text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl md:mb-20">
                        Four moves. <span className="text-outline">Zero chaos.</span>
                    </h2>
                </Reveal>
                <div>
                    {PROCESS.map((p, i) => (
                        <Reveal key={p.n} delay={i * 0.05}>
                            <div className="grid grid-cols-1 gap-4 border-t border-white/10 py-10 md:grid-cols-12 md:gap-8 md:py-14">
                                <span className="font-mono-x text-sm tracking-[0.2em] text-zinc-500 md:col-span-2">
                                    [{p.n}]
                                </span>
                                <h3 className="font-display text-3xl font-bold tracking-tight text-white md:col-span-4 md:text-4xl">
                                    {p.title}
                                </h3>
                                <p className="max-w-xl text-base leading-relaxed text-zinc-400 md:col-span-6 md:text-lg">
                                    {p.body}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                    <div className="border-t border-white/10" />
                </div>
            </section>

            {/* Why RaRe / proof */}
            <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32">
                <div className="grid gap-16 md:grid-cols-12">
                    <div className="md:col-span-4">
                        <Reveal><Overline>[ Why RaRe ]</Overline></Reveal>
                    </div>
                    <div className="md:col-span-8">
                        <Reveal>
                            <h2 className="font-display max-w-3xl text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
                                Product discipline meets growth accountability.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-10 max-w-3xl text-base leading-relaxed text-zinc-400 md:text-xl">
                                Take one recent build: an online fitness coach came to us
                                with clients but no system. We built his website, automated
                                lead capture from Instagram and the web, and ran onboarding
                                end-to-end — so his calendar filled without hiring a single
                                staff member.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <div className="mt-14 flex flex-wrap gap-3">
                                {PRINCIPLES.map((principle) => (
                                    <span
                                        key={principle}
                                        className="rounded-full border border-white/10 px-4 py-2 font-mono-x text-[11px] uppercase tracking-[0.2em] text-zinc-400"
                                    >
                                        {principle}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32">
                <Reveal><Overline>[ Next step ]</Overline></Reveal>
                <h2 className="font-display font-black uppercase leading-[0.9] tracking-tighter text-[13vw] lg:text-[8vw]">
                    <Reveal delay={0.05}><span className="block">Let's map your</span></Reveal>
                    <Reveal delay={0.12}>
                        <span className="block">
                            <span className="font-logo font-extrabold">launch.</span>
                        </span>
                    </Reveal>
                </h2>
                <div className="mt-16 flex flex-col gap-10 md:mt-20 md:flex-row md:items-center md:justify-between">
                    <Reveal>
                        <button
                            data-testid="pitch-book-call-cta"
                            onClick={openBooking}
                            className="group inline-flex items-center gap-4 rounded-full bg-white px-10 py-6 font-display text-xl font-bold text-[#050505] transition-transform duration-300 hover:scale-[1.04] md:px-14 md:py-8 md:text-3xl"
                        >
                            Book a Call
                            <span className="grid h-9 w-9 place-items-center rounded-full bg-[#050505] text-white transition-transform duration-300 group-hover:rotate-45 md:h-12 md:w-12">
                                <ArrowUpRight className="h-4 w-4 md:h-6 md:w-6" />
                            </span>
                        </button>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <a
                            data-testid="pitch-email-link"
                            href={`mailto:${EMAIL}`}
                            className="group flex items-center gap-4 text-white"
                        >
                            <span className="grid h-12 w-12 place-items-center rounded-full border border-white/15 transition-colors duration-300 group-hover:border-white/50 group-hover:bg-white/5">
                                <Mail className="h-5 w-5" />
                            </span>
                            <span className="font-display text-xl font-bold tracking-tight md:text-2xl">
                                {EMAIL}
                            </span>
                        </a>
                    </Reveal>
                </div>
            </section>

            <footer className="flex items-center justify-between border-t border-white/10 px-6 py-10 md:px-12">
                <p className="font-display text-lg font-black tracking-tight text-white">
                    RaRe<span className="align-super text-[0.5em] text-white/40">®</span>
                </p>
                <button
                    data-testid="pitch-footer-home"
                    onClick={() => navigate("/")}
                    className="flex items-center gap-2 font-mono-x text-[11px] uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-300 hover:text-white"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to site
                </button>
            </footer>
        </div>
    );
};

export const Pitch = () => {
    const [unlocked, setUnlocked] = useState(
        () => sessionStorage.getItem("rare_pitch_unlocked") === "1",
    );

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [unlocked]);

    return unlocked ? <Deck /> : <Gate onUnlock={() => setUnlocked(true)} />;
};
